import React, { useState } from 'react';
import { 
  Brain,
  Users,
  Bot,
  Search,
  Code,
  Smartphone,
  BarChart,
  ShoppingBag,
  ArrowRight,
  Zap,
  Shield,
  LineChart
} from 'lucide-react';
import SolutionsDialog from '../components/solutions/SolutionsDialog';

const services = [
  {
    icon: Brain,
    title: 'AI-Powered Websites',
    description: 'Smart websites that adapt to user behavior and drive conversions',
    features: [
      'Personalized user experiences',
      'Smart content adaptation',
      'Conversion optimization',
      'AI-driven analytics'
    ]
  },
  {
    icon: Bot,
    title: 'Chatbot Integration',
    description: 'Intelligent chatbots that provide 24/7 customer support',
    features: [
      'Natural language processing',
      'Automated responses',
      'Learning from interactions',
      'Seamless handoff to humans'
    ]
  },
  {
    icon: Search,
    title: 'SEO Optimization',
    description: 'AI-enhanced SEO strategies to improve your search rankings',
    features: [
      'Keyword optimization',
      'Content recommendations',
      'Performance tracking',
      'Competitor analysis'
    ]
  },
  {
    icon: Code,
    title: 'Custom Development',
    description: 'Tailored solutions built with cutting-edge technology',
    features: [
      'Custom web applications',
      'API integration',
      'Scalable architecture',
      'Performance optimization'
    ]
  },
  {
    icon: Smartphone,
    title: 'Mobile Solutions',
    description: 'Responsive designs that work perfectly on all devices',
    features: [
      'Mobile-first approach',
      'Progressive web apps',
      'Cross-platform compatibility',
      'Touch-optimized interfaces'
    ]
  },
  {
    icon: BarChart,
    title: 'Analytics Integration',
    description: 'Deep insights into your website performance and user behavior',
    features: [
      'Real-time tracking',
      'User behavior analysis',
      'Conversion tracking',
      'Custom reporting'
    ]
  },
  {
    icon: Shield,
    title: 'Security Solutions',
    description: 'Enterprise-grade security to protect your digital assets',
    features: [
      'SSL encryption',
      'DDoS protection',
      'Regular security audits',
      'Data protection'
    ]
  },
  {
    icon: LineChart,
    title: 'Growth Marketing',
    description: 'Data-driven strategies to accelerate your business growth',
    features: [
      'A/B testing',
      'Conversion optimization',
      'Marketing automation',
      'Performance tracking'
    ]
  }
];

export default function Services() {
  const [showConsultation, setShowConsultation] = useState(false);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white sm:text-5xl">
            Our Services
          </h1>
          <p className="mt-4 text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Transform your digital presence with our comprehensive suite of AI-powered solutions
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <Icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="ml-3 text-xl font-semibold text-gray-900 dark:text-white">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-center text-gray-600 dark:text-gray-300"
                    >
                      <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <div className="max-w-3xl mx-auto mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Customized Solutions for Your Business
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              We believe in providing tailored solutions that fit your specific needs and budget. 
              Contact us for a personalized consultation and custom quote.
            </p>
          </div>
          <button
            onClick={() => setShowConsultation(true)}
            className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-300"
          >
            Schedule a Consultation
            <ArrowRight className="ml-2 -mr-1 h-5 w-5" />
          </button>
        </div>

        <SolutionsDialog 
          isOpen={showConsultation}
          onClose={() => setShowConsultation(false)}
        />
      </div>
    </div>
  );
}